import { DateTime } from 'luxon'
import { Model, Module, Mutation, Action, store, ApiClient } from './helpers'
import { Locations } from './Locations'
import { Templates } from './Templates'
import { SlotVariationModel, SegmentFulfillment } from './interfaces'
import { translateToMins } from '../helpers/formats'

type AppointmentStatus = 'booked' | 'waiting'

export interface AppointmentModel {
  id: number
  begin_at: string
  checked_in_at: string
  status: AppointmentStatus
  external_id: string
  service_number: string
  checked_in: boolean
  client_name: string
  client_short_name: string
  chosen_time: ChosenTime
  employee_id: number
  location_id: number
  remaining_time: number
  resource_ids?: number[]
  slot_variations: SlotVariationModel[]
  waitlist_resources: Array<{
    resource_id: number,
  }>
}

export interface ChosenTime {
  begin_at: string
  deal_id: number
  employee_id: number
  end_at: string
  location_id: number
  manage: boolean
  manage_type: any
  segment_fulfillments: SegmentFulfillment[]
}

export interface WaitlistItem {
  id: number
  status: AppointmentStatus
  appointment: AppointmentModel
  ticket_number: string
  counter?: string
  resource_ids: number[] | null
  employee_id: number | null
  wait: number | null
  waitLabel: string
  standBy?: boolean
}

@Module
class AppointmentModule extends Model<AppointmentModel> {
  entitiesByResourceId: { [id: string]: AppointmentModel[] } = {}
  waitlist: WaitlistItem[] = [
    // { ticket_number: 'D657', counter: '01', wait: 5 },
    // { ticket_number: 'A123', counter: '05', wait: 6 },
    // { ticket_number: 'X987', counter: '03', wait: 7 },
    // { ticket_number: 'R464', counter: '02', wait: 2 },
    // { ticket_number: 'N24', counter: '01', wait: 2 },
    // { ticket_number: 'X988', counter: '02', wait: 2 },
    // { ticket_number: 'A124', counter: '04', wait: 2 },
    // { ticket_number: 'D658', counter: '02', wait: 2 },
    // { ticket_number: 'N25', counter: '06', wait: 2 },
  ]
  notificationQueue: WaitlistItem[] = [
    // { ticket_number: 'D657' },
  ]
  notificationQueueActiveItem: WaitlistItem = null
  notificationQueueProcessing = false
  notificationsAlreadyProcessed: { [key: number]: boolean } = {}

  @Action async GET_APPOINTMENTS() {
    try {
      const waitingResponse = await ApiClient.get('/my_client/appointments', {
        params: {
          statuses: 'waiting',
          // start_date: DateTime.local().startOf('day').toUTC().toISO(),
          // end_date: DateTime.local().endOf('day').toUTC().toISO(),
          // resource_ids: true,
          for_waitlist: true,
          /**
           * For some reason, this (with_booking_source: true)
           * actually gets appointments but with OUT OF DATE
           * remaining_time data
           */
          // with_booking_source: true,
          location_id: Locations.activeId,
          sort_field: 'sort_order',
          sort_order: 'asc',
        },
      })
      const checkedInResponse = await ApiClient.get('/my_client/appointments', {
        params: {
          statuses: 'booked,confirmed',
          checked_in: true,
          start_date: DateTime.local().startOf('day').toUTC().toISO(),
          end_date: DateTime.local().endOf('day').toUTC().toISO(),
          // start_date: '2022-01-13T08:00:00.000Z',
          // end_date: '2022-01-14T08:00:00.000Z',
          resource_ids: true,
          // for_waitlist: true,
          /**
           * For some reason, this (with_booking_source: true)
           * actually gets appointments but with OUT OF DATE
           * remaining_time data
           */
          // with_booking_source: true,
          location_id: Locations.activeId,
          sort_field: 'sort_order',
          sort_order: 'asc',
        },
      })

      const allAppts = [].concat(checkedInResponse.data.appointments).concat(waitingResponse.data.appointments)
      this.STORE_ENTITIES(allAppts)
      this.CALCULATE_WAITLIST()
      this.CALCULATE_NOTIFICATION_QUEUE()
      this.INDEX_ENTITIES({ key: 'entitiesByResourceId', indexBy: this.getResourceIdsFromAppointment })
    } catch (error) {
      /* tslint:disable-next-line */
      console.trace(error)
      // network error occured
    }
  }

  @Action RESET() {
    this.RESET_ALL()
  }

  @Mutation RESET_ALL() {
    this.entitiesByResourceId = {}
    this.waitlist = []
    this.notificationQueue = []
    this.notificationQueueActiveItem = null
    this.notificationQueueProcessing = false
    this.notificationsAlreadyProcessed = {}
  }

  @Action CALCULATE_NOTIFICATION_QUEUE() {
    this.waitlist.forEach((item) => {
      if (!item.standBy && item.wait === 0 && !this.notificationsAlreadyProcessed[item.id]) {
        this.ADD_APPOINTMENT_TO_NOTIFICATION_QUEUE(item)
      }
    })
    if (!this.notificationQueueProcessing) {
      // This item must have been pushed into a queue that wasn't processed yet, start processing
      this.PROCESS_NEXT_NOTIFICATION()
    }
  }

  @Action async PROCESS_NEXT_NOTIFICATION() {
    this.SET_ACTIVE_NOTIFICATION()
    setTimeout(() => {
      this.UNSET_ACTIVE_NOTIFICATION()
      if (this.notificationQueue.length) {
        this.PROCESS_NEXT_NOTIFICATION()
      } else {
        this.FINISH_NOTIFICATION_PROCESSING()
      }
    }, 10000) // TODO Should this be configurable?
  }

  @Mutation CALCULATE_WAITLIST() {
    const knownIds: any = {}
    this.waitlist = this.entities.reduce((waitlist: WaitlistItem[], appointment) => {
      // first create necessary waitlistItems from appointment events
      const item = this.generateWaitlistItem(appointment)
      // decide which items actually need to be added in the waitlist
      const shouldAddItemToWaitlist = this.decideIfItemShouldBeInWaitlist(item)
      if (shouldAddItemToWaitlist) {
        waitlist.push(item)
      } else {
        // console.log('filtered out a waitlist item', item)
      }
      return waitlist
    }, [])
      .sort((a, b) => {
        const {
          wait: waitA,
          appointment: {
            begin_at: beginAtA,
            checked_in_at: checkedInAtA,
          },
        } = a
        const {
          wait: waitB,
          appointment: {
            begin_at: beginAtB,
            checked_in_at: checkedInAtB,
          },
        } = b
        if (waitA === null || waitB === null) {
          // can't compare by remaining wait time
          return 0
        }

        if (waitA === waitB && beginAtA && beginAtB) {
          // sort by appointment begin at to see which actually starts first
          let dateA = new Date(beginAtA)
          let dateB = new Date(beginAtB)

          if (beginAtA === beginAtB) {
            // we need to sort by checked in at
            dateA = new Date(checkedInAtA)
            dateB = new Date(checkedInAtB)
          }

          // sort by begin at
          if (dateA > dateB) {
            return 1
          }

          if (dateA === dateB) {
            return 0
          }

          return -1
        }

        // default to sorting by remaining wait time for each waitlist item
        return a.wait - b.wait
      })
      .reduce((waitlist: WaitlistItem[], waitlistItem: WaitlistItem) => {
        // finally add the status based on waitlist item order & staff
        const item = this.generateWaitlistItemDisplayLabel(waitlistItem, knownIds)
        waitlist.push(item)
        return waitlist
      }, [])
  }

  @Mutation ADD_APPOINTMENT_TO_NOTIFICATION_QUEUE(item: WaitlistItem) {
    this.notificationQueue.push(item)
    this.notificationsAlreadyProcessed[item.id] = true
  }

  @Mutation SET_ACTIVE_NOTIFICATION() {
    const itemToDisplay = this.notificationQueue.shift()
    this.notificationQueueProcessing = true
    this.notificationQueueActiveItem = itemToDisplay
  }

  @Mutation UNSET_ACTIVE_NOTIFICATION() {
    this.notificationQueueActiveItem = undefined
  }

  @Mutation FINISH_NOTIFICATION_PROCESSING() {
    this.notificationQueueProcessing = false
  }

  /**
   * Convert an appointment into a simplified waitlist item ready to render
   * @param appointment an appointment
   */

  generateWaitlistItem(appointment: AppointmentModel): WaitlistItem {
    const resourceIds = this.getResourceIdsFromAppointment(appointment)
    const {
      checked_in,
      remaining_time,
    } = appointment
    const wait = checked_in ? 0 : remaining_time === null ? null : Math.floor(remaining_time / 60)

    return {
      appointment,
      id: appointment.id,
      resource_ids: resourceIds,
      employee_id: appointment.employee_id,
      status: appointment.status,
      ticket_number: appointment.client_short_name ||
        appointment.client_name ||
        appointment.service_number ||
        String(appointment.id), // TODO No external IDs setup
      wait,
      waitLabel: '',
    }
  }
  generateWaitlistItemDisplayLabel(waitlistItem: WaitlistItem, knownIds: any): WaitlistItem {
    const wasKnown = knownIds[waitlistItem.employee_id]
    knownIds[waitlistItem.employee_id] = true
    const wait = waitlistItem.wait

    return {
      ...waitlistItem,
      standBy: wasKnown,
      waitLabel: wait === null ? 'Unknown' : (wait > 0 ? `${translateToMins(wait * 60 * 1000, 'm', 'h')}` : (wasKnown ? 'Standby' : 'Your Turn')),
    }
  }

  /**
   * Base on settings in the current, active template and more, decide if
   * a waitlist item belongs on the list or not
   * @param item a waitlist item
   */
  decideIfItemShouldBeInWaitlist(item: WaitlistItem): boolean {
    // if (item.status !== 'waiting') { return false }
    // if (item.wait === null) { return false } // TODO Should filter, still getting lots of bad data ATM
    if (Templates.activeId) {
      const activeTemplate = Templates.entitiesById[Templates.activeId]
      const { data } = activeTemplate
      switch (data.waitlist_based_on) {
        case 'resources':
          if (!item.resource_ids) { return false }
          if (!data.waitlist_item_ids.some((id) => item.resource_ids.includes(id))) { return false }
          break
        case 'all':
          // Do nothing, there's no chance of filtering out here
          break
      }
    }
    if (Locations.activeId && item.appointment.location_id !== Locations.activeId) { return false }
    return true
  }

  getResourceIdsFromAppointment(appointment: AppointmentModel): number[] {
    if (appointment.resource_ids && appointment.checked_in) {
      return appointment.resource_ids
    }
    const { waitlist_resources } = appointment
    return (waitlist_resources || []).reduce((resourceIds, waitlistResource) => {
      return resourceIds.concat(waitlistResource.resource_id)
    }, [])
  }
}

export const Appointments = new AppointmentModule({ store, name: 'Appointments' })
